import React, { useState } from "react";

import { useTranslation } from "utils";

import { Button, CheckboxSegmented, CropFilterEmptyState, Divider, Flex, Icon, Popover, Tooltip, Typography } from "components";

export enum CROP_FILTER_TAB {
  MAIN = "MAIN",
  COVER = "COVER",
  PREVIOUS = "PREVIOUS",
}

export type CropFilterOption = {
  id: string;
  name: string;
};

type CropFilterChipProps = {
  mainCropOptions: CropFilterOption[];
  coverCropOptions: CropFilterOption[];
  // presence of this prop is what decides whether the PREVIOUS tab renders at all
  previousCropOptions?: CropFilterOption[];
  appliedCropIds: string[];
  appliedPreviousCropIds?: string[];
  onApply: (cropIds: string[], previousCropIds: string[]) => void;
  isDisabled?: boolean;
};

// trigger/tab/empty-state copy is identical across every caller of this chip, so it's owned here rather than
// threaded through as props (see global.json's cropFilterChip section)
export const CropFilterChip = ({
  mainCropOptions,
  coverCropOptions,
  previousCropOptions,
  appliedCropIds,
  appliedPreviousCropIds,
  onApply,
  isDisabled = false,
}: CropFilterChipProps) => {
  const { t } = useTranslation("global");
  const hasPreviousTab = !!previousCropOptions;

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CROP_FILTER_TAB>(CROP_FILTER_TAB.MAIN);
  const [internalCropIds, setInternalCropIds] = useState<string[]>([]);
  const [internalPreviousCropIds, setInternalPreviousCropIds] = useState<string[]>([]);

  const appliedCount = appliedCropIds.length + (appliedPreviousCropIds?.length ?? 0);
  const allOptionsById = [...mainCropOptions, ...coverCropOptions, ...(previousCropOptions ?? [])];
  const appliedNamesTooltip = [...appliedCropIds, ...(appliedPreviousCropIds ?? [])]
    .map((cropId) => allOptionsById.find((option) => option.id === cropId)?.name)
    .filter(Boolean)
    .join(", ");

  const handleOpenChange = (open: boolean) => {
    if (isDisabled) return;
    if (open) {
      setInternalCropIds(appliedCropIds);
      setInternalPreviousCropIds(appliedPreviousCropIds ?? []);
      setActiveTab(CROP_FILTER_TAB.MAIN);
    }
    setIsOpen(open);
  };

  const optionsForActiveTab =
    activeTab === CROP_FILTER_TAB.MAIN ? mainCropOptions : activeTab === CROP_FILTER_TAB.COVER ? coverCropOptions : previousCropOptions ?? [];
  const internalIdsForActiveTab = activeTab === CROP_FILTER_TAB.PREVIOUS ? internalPreviousCropIds : internalCropIds;
  const allOptionLabelForActiveTab =
    activeTab === CROP_FILTER_TAB.MAIN
      ? t("cropFilterChip.allMain")
      : activeTab === CROP_FILTER_TAB.COVER
      ? t("cropFilterChip.allCover")
      : t("cropFilterChip.allPrevious");
  const emptyMessageForActiveTab =
    activeTab === CROP_FILTER_TAB.MAIN
      ? t("cropFilterChip.noMainCrops")
      : activeTab === CROP_FILTER_TAB.COVER
      ? t("cropFilterChip.noCoverCrops")
      : t("cropFilterChip.noPreviousCrops");
  const allSelectedForActiveTab = optionsForActiveTab.length > 0 && optionsForActiveTab.every((option) => internalIdsForActiveTab.includes(option.id));

  const toggleCropId = (cropId: string) => {
    const setInternalIdsForActiveTab = activeTab === CROP_FILTER_TAB.PREVIOUS ? setInternalPreviousCropIds : setInternalCropIds;
    setInternalIdsForActiveTab((prev) => (prev.includes(cropId) ? prev.filter((id) => id !== cropId) : [...prev, cropId]));
  };

  const toggleAllForActiveTab = () => {
    const allIds = optionsForActiveTab.map((option) => option.id);
    if (allIds.length === 0) return;

    const setInternalIdsForActiveTab = activeTab === CROP_FILTER_TAB.PREVIOUS ? setInternalPreviousCropIds : setInternalCropIds;
    const hasAll = allIds.every((id) => internalIdsForActiveTab.includes(id));
    setInternalIdsForActiveTab((prev) => (hasAll ? prev.filter((id) => !allIds.includes(id)) : Array.from(new Set([...prev, ...allIds]))));
  };

  const handleClear = () => {
    setInternalCropIds([]);
    setInternalPreviousCropIds([]);
  };

  const handleApply = () => {
    onApply(internalCropIds, internalPreviousCropIds);
    setIsOpen(false);
  };

  return (
    <Flex className={`field-filter-chip ${appliedCount > 0 ? "active" : ""} ${isDisabled ? "disabled" : ""}`}>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        trigger="click"
        placement="bottomLeft"
        className={`filter-chip-popover crop-tabs-filter-popover ${hasPreviousTab ? "crop-tabs-filter-popover-wide" : ""}`}
        content={
          <Flex vertical gap="1rem" className="crop-tabs-filter-content">
            <Typography variant="paragraph-m" bold className="crop-tabs-filter-title">
              {t("cropFilterChip.popoverTitle")}
            </Typography>
            <Flex className="crop-tabs-filter-tabs" gap="0">
              <div
                role="button"
                className={`crop-tabs-filter-tab ${activeTab === CROP_FILTER_TAB.MAIN ? "active" : ""}`}
                onClick={() => setActiveTab(CROP_FILTER_TAB.MAIN)}
                data-testid="crop-tab-main"
              >
                {t("cropFilterChip.mainTab")}
              </div>
              <div
                role="button"
                className={`crop-tabs-filter-tab ${activeTab === CROP_FILTER_TAB.COVER ? "active" : ""}`}
                onClick={() => setActiveTab(CROP_FILTER_TAB.COVER)}
                data-testid="crop-tab-cover"
              >
                {t("cropFilterChip.coverTab")}
              </div>
              {hasPreviousTab && (
                <div
                  role="button"
                  className={`crop-tabs-filter-tab ${activeTab === CROP_FILTER_TAB.PREVIOUS ? "active" : ""}`}
                  onClick={() => setActiveTab(CROP_FILTER_TAB.PREVIOUS)}
                  data-testid="crop-tab-previous"
                >
                  {t("cropFilterChip.previousTab")}
                </div>
              )}
            </Flex>
            {optionsForActiveTab.length > 0 ? (
              <Flex className="crop-tabs-filter-checkboxes" wrap="wrap" gap="0.5rem" data-testid="crop-checkboxes-container">
                <CheckboxSegmented label={allOptionLabelForActiveTab} checked={allSelectedForActiveTab} onChange={toggleAllForActiveTab} />
                {optionsForActiveTab.map((option) => (
                  <CheckboxSegmented
                    key={option.id}
                    label={option.name}
                    checked={internalIdsForActiveTab.includes(option.id)}
                    onChange={() => toggleCropId(option.id)}
                    data-testid={`crop-checkbox-${option.id}`}
                  />
                ))}
              </Flex>
            ) : (
              <CropFilterEmptyState dataTestId="crop-tabs-filter-empty" message={emptyMessageForActiveTab} />
            )}
            <Divider className="crop-tabs-filter-divider" />
            <Flex align="center" justify="space-between" className="crop-tabs-filter-footer">
              <Button type="text" size="small" onClick={handleClear}>
                {t("cropFilterChip.clearAll")}
              </Button>
              <Button type="primary" size="small" onClick={handleApply}>
                {t("cropFilterChip.apply")}
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Flex className="field-filter-chip-trigger" align="center" gap="0.5rem" role="button" tabIndex={0}>
          <Typography variant="paragraph-m" noWrap className="margin-0">
            {t("cropFilterChip.trigger")}
          </Typography>
          {appliedCount > 0 &&
            (appliedNamesTooltip ? (
              <Tooltip placement="top" title={appliedNamesTooltip}>
                <Flex className="selected-filter-options-count" align="center" justify="center">
                  <Typography variant="paragraph-s" bold className="margin-0">
                    {appliedCount}
                  </Typography>
                </Flex>
              </Tooltip>
            ) : (
              <Flex className="selected-filter-options-count" align="center" justify="center">
                <Typography variant="paragraph-s" bold className="margin-0">
                  {appliedCount}
                </Typography>
              </Flex>
            ))}
          <Icon iconName="tabler/chevron-down" size="s" />
        </Flex>
      </Popover>
    </Flex>
  );
};
